import { Controller } from '@hotwired/stimulus';
import * as outbox from '../utils/outbox';

// The reorder screen's +/- buttons and running total. The server prices the
// order; this only shows the customer what they are about to send.
//
// It also works with no signal: Send keeps the order on the phone (the
// outbox) and it goes out once there is signal; search runs on the catalog
// saved on the phone. The cut-off is Bud's: an order that arrives late is
// refused by the server, and this screen says so with Bud's number.
export default class extends Controller {
  static targets = [
    'line',
    'quantity',
    'sum',
    'count',
    'send',
    'list',
    'search',
    'results',
    'banner',
  ];

  static values = {
    location: String,
    date: String,
    deadline: String,
    day: String,
    catalog: String,
    prices: Boolean,
  };

  connect() {
    this.total();
    this.changed = () => this.showBanner();
    this.result = (event) => this.onResult(event.detail);
    window.addEventListener('outbox:changed', this.changed);
    window.addEventListener('outbox:result', this.result);
    this.tick = setInterval(() => this.showBanner(), 30000);
    this.showBanner();
    this.loadCatalog();
  }

  disconnect() {
    window.removeEventListener('outbox:changed', this.changed);
    window.removeEventListener('outbox:result', this.result);
    clearInterval(this.tick);
  }

  // ---- sending, with or without signal ----

  async send(event) {
    event.preventDefault();
    const form = event.target;
    const fields = {};
    new FormData(form).forEach((value, key) => {
      fields[key] = value;
    });
    // One waiting order per store and day: a new send replaces the old one.
    const mine = (await outbox.all().catch(() => [])).filter((e) =>
      this.isMine(e),
    );
    await Promise.all(mine.map((e) => outbox.remove(e.id)));
    const entry = {
      id: outbox.newId(),
      kind: 'order',
      url: form.action,
      fields,
      location: this.locationValue,
      date: this.dateValue,
      deadline: this.deadlineValue,
      day: this.dayValue,
    };
    this.sendTarget.disabled = true;
    this.sendTarget.dataset.label ||= this.sendTarget.textContent;
    this.sendTarget.textContent = 'Sending…';
    // Sent from here while the page is open; the background only takes over
    // with no signal, so the two never race for the same order.
    await outbox.keep(entry, { background: false });
    const result = navigator.onLine
      ? await outbox.send(entry)
      : { state: 'offline' };
    if (result.state === 'offline') outbox.registerBackground();
    this.onResult({ entry, ...result });
  }

  onResult({ entry, state, data }) {
    if (!this.isMine(entry)) return;
    if (state === 'sent' && data?.url) {
      window.location.assign(data.url);
      return;
    }
    this.sendTarget.textContent =
      this.sendTarget.dataset.label || this.sendTarget.textContent;
    this.total();
    this.showBanner();
  }

  isMine(entry) {
    return (
      entry.kind === 'order' &&
      entry.location === this.locationValue &&
      entry.date === this.dateValue
    );
  }

  async showBanner() {
    if (!this.hasBannerTarget) return;
    const entry = (await outbox.all().catch(() => [])).find((e) =>
      this.isMine(e),
    );
    if (!entry) {
      this.bannerTarget.innerHTML = '';
      return;
    }
    const left = this.timeLeft(entry.deadline);
    if (entry.status === 'refused' || !left) {
      const why =
        entry.status === 'refused'
          ? entry.message
          : 'The cut-off passed before this order found signal.';
      this.bannerTarget.innerHTML = `<div class="mx-5 mt-4 rounded-xl bg-red-50 p-3 text-sm text-red-800">
        <p class="font-bold">Your ${entry.day} order didn't reach Bud's.</p>
        <p class="mt-1">${this.escape(
          why,
        )} Call Bud's at <a class="font-bold underline" href="tel:2707654452">270-765-4452</a>. Your order is still below so you can read it out.</p>
        <button type="button" class="mt-2 text-xs font-semibold underline" data-action="reorder#discard">Remove it from this phone</button></div>`;
    } else {
      this.bannerTarget.innerHTML = `<div class="mx-5 mt-4 rounded-xl bg-yellow-50 p-3 text-sm text-yellow-900">
        <p class="font-bold">Saved on this phone. It sends when you have signal.</p>
        <p class="mt-1">It must reach Bud's by ${this.deadlineLabel(
          entry.deadline,
        )} (${left} left). On iPhone, open Bud's when you have signal.</p></div>`;
    }
  }

  async discard() {
    const mine = (await outbox.all().catch(() => [])).filter((e) =>
      this.isMine(e),
    );
    await Promise.all(mine.map((e) => outbox.remove(e.id)));
    window.dispatchEvent(new CustomEvent('outbox:changed'));
  }

  timeLeft(deadline) {
    const ms = Date.parse(deadline) - Date.now();
    if (!(ms > 0)) return null;
    const minutes = Math.floor(ms / 60000);
    return minutes >= 60
      ? `${Math.floor(minutes / 60)} h ${minutes % 60} min`
      : `${minutes} min`;
  }

  deadlineLabel(deadline) {
    const at = new Date(deadline);
    return `${at.toLocaleTimeString([], {
      hour: 'numeric',
      minute: '2-digit',
    })} ${at.toLocaleDateString([], { weekday: 'long' })}`;
  }

  // Safe in text and in attributes (quotes too).
  escape(text) {
    return String(text ?? '').replace(
      /[&<>"']/g,
      (c) =>
        ({
          '&': '&amp;',
          '<': '&lt;',
          '>': '&gt;',
          '"': '&quot;',
          "'": '&#39;',
        }[c]),
    );
  }

  // ---- the catalog on the phone ----

  async loadCatalog() {
    if (!this.catalogValue) return;
    try {
      const response = await fetch(this.catalogValue, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (response.ok) this.catalog = (await response.json()).products;
    } catch {
      // No signal and never saved: search asks the server when there is signal.
    }
  }

  localResults(query) {
    const words = query.toLowerCase().split(/\s+/).filter(Boolean);
    const found = this.catalog
      .filter((p) => {
        const text = `${p.title} ${p.size || ''} ${p.name}`.toLowerCase();
        return words.every((w) => text.includes(w));
      })
      .slice(0, 15);
    if (found.length === 0)
      return '<p class="px-5 py-3 text-sm text-gray-500">Nothing found. Try another word, or call Bud\'s.</p>';
    return `<ul class="divide-y divide-gray-100">${found
      .map((p) => {
        const detail = [
          p.size,
          this.pricesValue && p.price !== null ? this.money(p.price) : null,
        ]
          .filter(Boolean)
          .join(' · ');
        const line = this.lineHtml(p, detail);
        return `<li class="flex items-center gap-3 px-5 py-3">
        <div class="min-w-0 flex-1"><p class="truncate font-semibold text-gray-900">${this.escape(
          p.title,
        )}</p>
        <p class="text-xs text-gray-500">${this.escape(detail)}</p></div>
        <button type="button" class="rounded-full bg-primary-800 px-4 py-2 text-sm font-bold text-white" data-action="reorder#add" data-line="${this.escape(
          line,
        )}">Add</button></li>`;
      })
      .join('')}</ul>`;
  }

  // Same markup as reorders/_line, for a product found on the phone.
  lineHtml(p, detail) {
    const title = this.escape(p.title);
    const price =
      this.pricesValue && p.price !== null ? ` data-price="${p.price}"` : '';
    return `<li class="flex items-center gap-3 px-5 py-3" data-reorder-target="line"${price} data-product-id="${
      p.id
    }">
      <div class="min-w-0 flex-1"><p class="truncate font-semibold text-gray-900">${title}</p><p class="text-xs text-gray-500">${this.escape(
      detail,
    )}</p></div>
      <div class="flex items-center gap-1">
        <button type="button" class="h-11 w-11 rounded-full bg-gray-100 text-xl" aria-label="Less ${title}" data-action="reorder#less">−</button>
        <input type="number" min="0" inputmode="numeric" name="quantities[${
          p.id
        }]" value="1" aria-label="${title} quantity" data-reorder-target="quantity" data-action="input->reorder#total" class="w-12 border-0 bg-transparent p-0 text-center text-lg font-bold tabular-nums focus:ring-0">
        <button type="button" class="h-11 w-11 rounded-full bg-primary-100 text-xl text-primary-900" aria-label="More ${title}" data-action="reorder#more">+</button>
      </div></li>`;
  }

  money(n) {
    return Number(n).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
    });
  }

  more(event) {
    this.step(event, 1);
  }

  less(event) {
    this.step(event, -1);
  }

  step(event, delta) {
    const input = event.target.closest('li').querySelector('input');
    input.value = Math.max(0, (parseInt(input.value, 10) || 0) + delta);
    this.total();
  }

  // Enter in the search box searches; it must not send the order.
  keep(event) {
    event.preventDefault();
  }

  // Searches as the customer types, a moment after they stop.
  search() {
    clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(async () => {
      const query = this.searchTarget.value.trim();
      if (this.catalog) {
        this.resultsTarget.innerHTML =
          query.length < 2 ? '' : this.localResults(query);
        return;
      }
      const url = new URL(
        this.searchTarget.dataset.url,
        window.location.origin,
      );
      url.searchParams.set('q', this.searchTarget.value);
      try {
        const response = await fetch(url, { headers: { Accept: 'text/html' } });
        this.resultsTarget.innerHTML = response.ok ? await response.text() : '';
      } catch {
        this.resultsTarget.innerHTML =
          '<p class="px-5 py-3 text-sm text-gray-500">Search needs signal the first time. Try again when you have signal.</p>';
      }
    }, 250);
  }

  // Adds a searched product to the order with one case, or one more case if
  // it is already there.
  add(event) {
    const template = document.createElement('template');
    template.innerHTML = event.currentTarget.dataset.line.trim();
    const line = template.content.firstElementChild;
    const existing = this.lineTargets.find(
      (l) => l.dataset.productId === line.dataset.productId,
    );
    if (existing) {
      const input = existing.querySelector('input');
      input.value = (parseInt(input.value, 10) || 0) + 1;
    } else {
      this.listTarget.appendChild(line);
    }
    this.searchTarget.value = '';
    this.resultsTarget.innerHTML = '';
    this.total();
  }

  clear() {
    this.quantityTargets.forEach((input) => (input.value = 0));
    this.total();
  }

  total() {
    let sum = 0;
    let lines = 0;
    let cases = 0;
    this.lineTargets.forEach((line) => {
      const qty = parseInt(line.querySelector('input').value, 10) || 0;
      if (qty > 0) {
        lines += 1;
        cases += qty;
        sum += qty * (parseFloat(line.dataset.price) || 0);
      }
    });
    this.sumTarget.textContent = sum.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
    });
    this.countTarget.textContent = `${lines} items · ${cases} cases`;
    this.sendTarget.disabled =
      lines === 0 || this.sendTarget.dataset.locked === 'true';
  }
}
